import { isDefined } from '@sgme/fp';
import { GridCheckBoxColumn } from 'components/common-form/builder/form/node/field/SimpleGrid/GridCheckBoxColumn';
import { YesNoRadioButtons } from 'components/shared/ButtonsGroup/YesNoRadioButtons';
import type { FieldMetadata } from 'models/forms/fields';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { FC, useCallback } from 'react';

import { UnknownFieldWarning } from '../UnknownField';
import { GridClientColumn } from './GridClientColumn';
import { GridDateColumn } from './GridDateColumn';
import { GridListColumn } from './GridListColumn';
import { GridPercentColumn } from './GridPercentColumn';
import { GridTextColumn } from './GridTextColumn';

export type TableRowData = Record<string, unknown>;

interface TableColumnsProps {
  row: TableRowData;
  rowIndex: number;
  fieldId: string;

  metadata: FieldMetadata[];

  setRowValue: (rowIndex: number, value: TableRowData) => void;
}

export interface TableColumnProps<T> {
  value: T;
  id: string;
  setCellValue: (id: string, value: T) => void;
}
export function getDefaultValueForNewTableRow(meta: FieldMetadata) {
  switch (meta.type) {
    case 'ListField':
      return (meta.availableItems?.length ?? 0) > 0 ? meta.availableItems[0].value : null;
    default:
      return null;
  }
}

export const TableGridColumns: FC<TableColumnsProps> = ({ row, metadata, rowIndex, setRowValue }) => {
  const onChange = useCallback(
    (id: string, value: unknown) => {
      setRowValue(rowIndex, { ...row, [id]: value });
    },
    [row, rowIndex, setRowValue],
  );
  return (
    <>
      {metadata
        .filter(isDefined)
        .map(columnMetadata => {
          const { id } = columnMetadata;
          const value = row[id];

          switch (columnMetadata.type) {
            case 'DateField':
              return (
                <GridDateColumn
                  key={id}
                  id={id}
                  value={value as Nullable<string>}
                  setCellValue={onChange}
                  isClearable={columnMetadata.isClearable ?? false}
                />
              );
            case 'PercentField':
              return (
                <GridPercentColumn
                  key={id}
                  id={id}
                  value={value as string}
                  className="min-width-10em"
                  setCellValue={onChange}
                  placeholderKey={columnMetadata.placeholderKey}
                />
              );
            case 'ClientListField':
              return (
                <GridClientColumn
                  key={id}
                  id={id}
                  value={value as Nullable<ISalesCounterpart>}
                  setCellValue={onChange}
                />
              );

            case 'ListField':
              const availableItems = columnMetadata.availableItems!;
              return (
                <GridListColumn
                  key={id}
                  id={id}
                  value={value as Nullable<string>}
                  availableItems={availableItems}
                  setCellValue={onChange}
                />
              );
            case 'TextField':
              return (
                <GridTextColumn
                  key={id}
                  id={id}
                  value={value as string}
                  setCellValue={onChange}
                  placeholderKey={columnMetadata.placeholderKey}
                />
              );
            case 'CheckboxField':
              return <YesNoRadioButtons key={id} id={id} value={value as boolean} onSelect={v => onChange(id, v)} />;
            case 'ToggleField':
              return <GridCheckBoxColumn key={id} id={id} value={value as boolean} setCellValue={onChange} />;
            default:
              return <UnknownFieldWarning key={id} id={columnMetadata.id} value={columnMetadata.type} />;
          }
        })
        .map(col => (
          <td key={col.key}>{col}</td>
        ))}
    </>
  );
};

TableGridColumns.displayName = 'TableGridColumns';
